import * as THREE from 'three';
import type { System } from '../../systems';
import type { EntityRegistry } from '../../entities';
import { Planet } from './Planet';
import { NpcShip } from '../npcs/NpcShip';
import { PlayerShip } from '../ships/PlayerShip';
import { TugShip } from '../rescue/TugShip';

interface OrbitTugTarget {
  readonly object3D: THREE.Object3D;
  readonly velocity: THREE.Vector3;
}

// Below this speed the ship is treated as fully "parked" and gets the full
// orbital tug. Above it the tug fades out linearly until it disappears, so
// pulling the throttle smoothly hands control back to the pilot.
const FULL_TUG_SPEED = 6;
const NO_TUG_SPEED = 22;

/** Carries idle ships along with the planet they're parked next to.
 *
 *  Why: when a ship just sits inside a planet's capture zone (mining a ring
 *  rock, hovering for a screenshot, etc.), the planet's orbital motion would
 *  otherwise leave it behind. We translate the ship by the planet's
 *  instantaneous orbital velocity, *without* writing into ship.velocity, so
 *  the moment the pilot accelerates they leave the planet behind naturally —
 *  no inherited orbital impulse to fight against. */
export class PlanetaryOrbitCapture implements System {
  readonly id = 'planetary-orbit-capture';

  private readonly tmpPlanetVel = new THREE.Vector3();
  private readonly tmpPlanetPos = new THREE.Vector3();

  constructor(private readonly entities: EntityRegistry) {}

  update(dt: number): void {
    const planets: Planet[] = [];
    const targets: OrbitTugTarget[] = [];

    for (const entity of this.entities.all().values()) {
      if (!entity.isAlive) continue;
      if (entity instanceof Planet) planets.push(entity);
      else if (
        entity instanceof NpcShip ||
        entity instanceof PlayerShip ||
        entity instanceof TugShip
      ) {
        // The recovery tug rides along with whatever planet the
        // stranded player is parked next to — otherwise the player
        // drifts with the planet's orbital velocity and the tug
        // never closes the last few units.
        targets.push(entity);
      }
    }

    if (planets.length === 0 || targets.length === 0) return;

    for (const ship of targets) {
      const speed = ship.velocity.length();
      if (speed >= NO_TUG_SPEED) continue;

      const nearest = this.nearestPlanetInRange(ship, planets);
      if (!nearest) continue;

      // Linear falloff: full tug at FULL_TUG_SPEED, none at NO_TUG_SPEED.
      const idleFactor =
        speed <= FULL_TUG_SPEED
          ? 1
          : 1 - (speed - FULL_TUG_SPEED) / (NO_TUG_SPEED - FULL_TUG_SPEED);

      nearest.getOrbitalVelocity(this.tmpPlanetVel);
      ship.object3D.position.addScaledVector(this.tmpPlanetVel, dt * idleFactor);
    }
  }

  private nearestPlanetInRange(
    ship: OrbitTugTarget,
    planets: readonly Planet[]
  ): Planet | null {
    let nearest: Planet | null = null;
    let nearestDistSq = Infinity;
    for (const planet of planets) {
      planet.getWorldPosition(this.tmpPlanetPos);
      const distSq = ship.object3D.position.distanceToSquared(this.tmpPlanetPos);
      const captureSq = planet.captureRadius * planet.captureRadius;
      if (distSq > captureSq) continue;
      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearest = planet;
      }
    }
    return nearest;
  }
}
